import React, { useState } from 'react';
import {
    Box, Text, Button, HStack, VStack, Image, Icon, Badge, Spinner, Wrap, WrapItem,
    IconButton, Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalBody,
} from '@chakra-ui/react';
import { WarningTwoIcon, CheckCircleIcon, EditIcon, SearchIcon, MinusIcon, AddIcon } from '@chakra-ui/icons';
import { FaMagic } from 'react-icons/fa';

const ETAPAS = {
    fila: 'Aguardando a vez…',
    enviando: 'Enviando a foto…',
    processando: 'Removendo o fundo…',
};

const MODELO_PADRAO = 'u2net';
const MODELO_ALTERNATIVO = 'isnet-general-use';

const Opcao = ({ titulo, url, escolhida, onClick, onAmpliar, isDisabled }) => (
    <Box
        position="relative"
        flex="1"
        minW="120px"
        borderWidth="2px"
        borderColor={escolhida ? 'green.400' : 'gray.200'}
        bg={escolhida ? 'green.50' : 'white'}
        rounded="md"
        p={2}
        textAlign="center"
        opacity={isDisabled ? 0.6 : 1}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-pressed={escolhida}
        onClick={isDisabled ? undefined : onClick}
        onKeyDown={(e) => {
            if (isDisabled) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
        }}
    >
        {url && (
            <IconButton
                aria-label="Ampliar"
                icon={<SearchIcon />}
                size="xs"
                position="absolute"
                top={1}
                right={1}
                zIndex={1}
                colorScheme="blackAlpha"
                onClick={(e) => { e.stopPropagation(); onAmpliar(url, titulo); }}
            />
        )}
        <HStack justify="center" spacing={1} mb={1}>
            {escolhida && <Icon as={CheckCircleIcon} color="green.500" boxSize={3} />}
            <Text fontSize="xs" fontWeight="bold" color={escolhida ? 'green.700' : 'gray.600'}>{titulo}</Text>
        </HStack>
        {url ? (
            <Image src={url} alt={titulo} maxH="150px" mx="auto" objectFit="contain" bg="gray.50" rounded="sm" />
        ) : (
            <Box h="150px" display="flex" alignItems="center" justifyContent="center">
                <Text fontSize="xs" color="gray.400">indisponível</Text>
            </Box>
        )}
    </Box>
);

const VisualizadorFoto = ({ foto, onFechar }) => {
    const [zoom, setZoom] = useState(1);

    return (
        <Modal isOpen={!!foto} onClose={onFechar} size="full" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent bg="gray.900" m={0} rounded="none">
                <ModalCloseButton
                    zIndex={2}
                    size="lg"
                    color="white"
                    bg="blackAlpha.800"
                    borderRadius="full"
                    boxShadow="dark-lg"
                    _hover={{ bg: 'black' }}
                    _active={{ bg: 'black' }}
                />
                <ModalBody p={0} display="flex" flexDirection="column">
                    <Box flex="1" overflow="auto" display="flex" alignItems="center" justifyContent="center" p={2}>
                        <Image
                            src={foto?.url}
                            alt={foto?.titulo}
                            w={`${zoom * 100}%`}
                            maxW="none"
                            objectFit="contain"
                            bg="white"
                            rounded="md"
                        />
                    </Box>
                    <HStack justify="center" spacing={3} py={3} bg="gray.800">
                        <IconButton aria-label="Diminuir" icon={<MinusIcon />} size="sm"
                            onClick={() => setZoom(z => Math.max(1, +(z - 0.5).toFixed(1)))} />
                        <Text color="white" fontSize="sm" minW="70px" textAlign="center">
                            {Math.round(zoom * 100)}%
                        </Text>
                        <IconButton aria-label="Aumentar" icon={<AddIcon />} size="sm" colorScheme="purple"
                            onClick={() => setZoom(z => Math.min(5, +(z + 0.5).toFixed(1)))} />
                        <Button size="sm" variant="outline" colorScheme="whiteAlpha" color="white"
                            onClick={() => setZoom(1)}>
                            Ajustar
                        </Button>
                    </HStack>
                    <Text color="whiteAlpha.800" fontSize="xs" textAlign="center" pb={3}>
                        {foto?.titulo} · arraste para percorrer a imagem
                    </Text>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

/**
 * Mostra, foto a foto, o que o processador tirou e o que a remocao de fundo fez.
 * Nada e publicado antes dele escolher; quando o recorte sai ruim ele fica com a
 * foto original ou tenta o outro modo, que usa um modelo diferente.
 */
const RevisaoFotosSemFundo = ({ previas = [], escolhas = {}, onEscolher, onTentarOutroModo, onEditar, processando = {} }) => {
    const [ampliada, setAmpliada] = useState(null);

    if (!previas.length) return null;

    const abrirAmpliada = (url, titulo) => setAmpliada({ url, titulo });

    return (
        <VStack spacing={3} align="stretch">
            <Box p={3} bg="blue.50" borderWidth="1px" borderColor="blue.200" rounded="md">
                <Text fontSize="xs" color="blue.800">
                    Confira cada foto antes de publicar. Se o recorte comeu a peça ou manteve a embalagem, use a foto original.
                </Text>
            </Box>

            {previas.map((previa, idx) => {
                const escolhaSemFundo = !!escolhas[previa.imagem_id];
                const temSemFundo = !!previa.url_sem_fundo;
                const emAndamento = ['fila', 'enviando', 'processando'].includes(previa.estado);
                const carregando = !!processando[previa.imagem_id] || emAndamento;
                const rotuloEtapa = ETAPAS[previa.estado] || 'Processando…';
                const jaTentouOutroModo = previa.modelo && previa.modelo !== MODELO_PADRAO;

                return (
                    <Box key={previa.chave ?? previa.imagem_id ?? idx} borderWidth="1px" borderColor="gray.200" rounded="lg" p={3} bg="white">
                        {previa.alerta && (
                            <HStack align="flex-start" mb={2} p={2} bg="orange.50" rounded="md" borderWidth="1px" borderColor="orange.200">
                                <Icon as={WarningTwoIcon} color="orange.500" mt={0.5} boxSize={3} />
                                <Text fontSize="xs" color="orange.800">{previa.alerta}</Text>
                            </HStack>
                        )}

                        {carregando ? (
                            <HStack justify="center" py={emAndamento ? 2 : 6}>
                                <Spinner size="sm" color={previa.estado === 'fila' ? 'gray.400' : 'purple.500'} />
                                <Text fontSize="xs" color="gray.600">{emAndamento ? `Foto ${idx + 1}: ${rotuloEtapa}` : 'Processando…'}</Text>
                            </HStack>
                        ) : previa.erro ? (
                            <HStack justify="center" py={2}>
                                <Icon as={WarningTwoIcon} color="red.500" boxSize={3} />
                                <Text fontSize="xs" color="red.600">{previa.erro}</Text>
                            </HStack>
                        ) : (
                            <HStack spacing={2} align="stretch">
                                <Opcao
                                    titulo={previa.editada ? 'Foto editada' : 'Foto original'}
                                    url={previa.url_original}
                                    escolhida={!escolhaSemFundo}
                                    onClick={() => onEscolher(previa.imagem_id, false)}
                                    onAmpliar={abrirAmpliada}
                                />
                                <Opcao
                                    titulo="Sem fundo"
                                    url={previa.url_sem_fundo}
                                    escolhida={escolhaSemFundo}
                                    isDisabled={!temSemFundo}
                                    onClick={() => temSemFundo && onEscolher(previa.imagem_id, true)}
                                    onAmpliar={abrirAmpliada}
                                />
                            </HStack>
                        )}

                        <Wrap mt={2} spacing={2} justify="space-between" align="center" display={emAndamento || previa.erro ? 'none' : undefined}>
                            <WrapItem>
                                <HStack spacing={2}>
                                    <Badge colorScheme={escolhaSemFundo ? 'green' : 'gray'} fontSize="10px">
                                        {escolhaSemFundo ? 'Vai publicar sem fundo' : 'Vai publicar a foto atual'}
                                    </Badge>
                                    {previa.editada && <Badge colorScheme="purple" fontSize="10px">editada</Badge>}
                                </HStack>
                            </WrapItem>
                            <WrapItem>
                                <HStack spacing={2}>
                                    {onEditar && (
                                        <Button
                                            size="xs"
                                            variant="outline"
                                            leftIcon={<EditIcon />}
                                            isDisabled={carregando}
                                            onClick={() => onEditar(previa)}
                                        >
                                            Recortar
                                        </Button>
                                    )}
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        colorScheme="purple"
                                        leftIcon={<Icon as={FaMagic} />}
                                        isDisabled={carregando || jaTentouOutroModo}
                                        onClick={() => onTentarOutroModo(previa.imagem_id, MODELO_ALTERNATIVO)}
                                    >
                                        {jaTentouOutroModo ? 'Outro modo já tentado' : 'Tentar outro modo'}
                                    </Button>
                                </HStack>
                            </WrapItem>
                        </Wrap>
                    </Box>
                );
            })}

            <VisualizadorFoto foto={ampliada} onFechar={() => setAmpliada(null)} />
        </VStack>
    );
};

export default RevisaoFotosSemFundo;
